import React, { useEffect, useState } from 'react';
import { KnytningsjekkResponse } from '../../utils/api';
import { useApptekster } from '../../contexts/ApptekstContext';
import { UseApiProps } from '../../hooks/useApi';
import { SVVSpinner } from '@komponentkassen/svv-spinner';
import { useKnytningState } from './KnyttSkilt';
import { useFeil } from '../../hooks/useFeil';
import { SVVTextLink } from '@komponentkassen/svv-link';
import { PakrevdFelt } from '../../components/PakrevdFelt';
import { SVVFormError, SVVSearchField } from '@komponentkassen/svv-form';

export type KjennemerkeSkjemaData = { sokefelt: string };

type HentKjoretoySkjemaProps = {
    api: UseApiProps<KnytningsjekkResponse>;
};
export const SjekkKjoretoySkjema: React.FC<HentKjoretoySkjemaProps> = ({
    api: { hent, laster, data, timestamp },
}) => {
    const rettighetId = useKnytningState((state) => state.rettighetId);
    const kjennemerke = useKnytningState((state) => state.kjennemerke);
    const setKjennemerke = useKnytningState((state) => state.setKjennemerke);
    const t = useApptekster();
    const [feilmelding, setFeilmelding] = useState('');
    const [feilmeldingFraServer, setFeilmeldingFraServer] = useState(false);
    const { tilFeilmelding } = useFeil({
        avregistert:
            'personlig-kjennemerke.knytning.sjekk.merknad.avregistrert',
        avregistert_bilforhandler:
            'personlig-kjennemerke.knytning.sjekk.merknad.avregistrert-bilforhandler',
        har_medeier: 'personlig-kjennemerke.knytning.sjekk.merknad.har-medeier',
        stjaalet: 'personlig-kjennemerke.knytning.sjekk.merknad.stjaalet',
        fant_ikke_kjoretoy:
            'personlig-kjennemerke.knytning.sjekk.merknad.fant-ikke-kjoretoy',
        har_begjaering:
            'personlig-kjennemerke.knytning.sjekk.merknad.har-begjaering',
        har_kjoreforbud:
            'personlig-kjennemerke.knytning.sjekk.merknad.har-kjoreforbud',
        ikke_registrert_eier:
            'personlig-kjennemerke.knytning.sjekk.merknad.ikke-registrert-eier',
        kjoringens_art_11:
            'personlig-kjennemerke.knytning.sjekk.merknad.kjoringens-art-11',
        mangler_forsikring:
            'personlig-kjennemerke.knytning.sjekk.merknad.mangler-forsikring',
        pk_knytning_finnes_allerede_paa_kjoretoy:
            'personlig-kjennemerke.knytning.sjekk.merknad.kjoretoy-allerede-knyttet',
        solgt: 'personlig-kjennemerke.knytning.sjekk.merknad.solgt',
        ugyldig_teknisk_kjoretoykode:
            'personlig-kjennemerke.knytning.sjekk.merknad.ugyldig-teknisk-kjoretoykode',
        utfort: 'personlig-kjennemerke.knytning.sjekk.merknad.utfort',
        vraket: 'personlig-kjennemerke.knytning.sjekk.merknad.vraket',
        kjoretoy_ikke_registrert_pa_ny_eier:
            'personlig-kjennemerke.knytning.sjekk.merknad.omregistrering-ikke-fullfort',
        generell: 'personlig-kjennemerke.knytning.sjekk.merknad.generell',
        annet: 'personlig-kjennemerke.knytning.sjekk.merknad.annet',
        default: 'personlig-kjennemerke.delt.validering.default',
    });

    useEffect(() => {
        if (timestamp && data?.valideringsfeil.length) {
            setFeilmeldingFraServer(true);
        }
    }, [data, setFeilmeldingFraServer, timestamp]);

    useEffect(() => {
        if (kjennemerke) {
            setFeilmelding('');
            setFeilmeldingFraServer(false);
            hent({ kjennemerke, rettighetId });
        }
    }, [hent, kjennemerke, rettighetId]);

    return (
        <div>
            <PakrevdFelt className="spacing-bottom--small" />
            <SVVSearchField
                id={'sok-skilt'}
                maxLength={8}
                label={t('personlig-kjennemerke.felles.kjoretoy.label')}
                placeholder={t(
                    'personlig-kjennemerke.felles.kjoretoy.placeholder',
                )}
                aria-invalid={feilmeldingFraServer}
                aria-describedby={
                    feilmeldingFraServer
                        ? 'tegnkombinasjon-feilmelding'
                        : undefined
                }
                errorMessage={feilmelding}
                onSearch={(e) => {
                    if (e.length === 0) {
                        setFeilmeldingFraServer(false);
                        setFeilmelding(
                            t(
                                'personlig-kjennemerke.bestill.kjoretoy.validering.paakrevd',
                            ),
                        );
                    } else if (!e.match(/^[a-zA-Z0-9 ]*$/)) {
                        setFeilmeldingFraServer(false);
                        setFeilmelding(
                            t(
                                'personlig-kjennemerke.bestill.kjoretoy.validering.ugyldig',
                            ),
                        );
                    } else {
                        setKjennemerke(e.toUpperCase());
                    }
                }}
                required
            />
            <div aria-live="assertive">
                {feilmeldingFraServer && !laster && (
                    <SVVFormError
                        className="medium-bredde spacing-top--xxsmall"
                        id="tegnkombinasjon-feilmelding"
                    >
                        <b>
                            {t(
                                'personlig-kjennemerke.knytning.feilmelding.overskrift',
                            )}
                        </b>
                        <ul>
                            {data?.valideringsfeil
                                .sort((feil) => {
                                    return feil === 'annet' ? 1 : -1; // Legg "annet" som nederste merknad
                                })
                                .map((feil) => {
                                    if (feil === 'skilt_passer_ikke') {
                                        // Denne består av flere tekster og en lenke, og funker ikke med tilFeilmelding
                                        return (
                                            <li key={feil}>
                                                {t(
                                                    'personlig-kjennemerke.knytning.sjekk.merknad.skilt-passer-ikke',
                                                )}{' '}
                                                <SVVTextLink
                                                    url={t(
                                                        'personlig-kjennemerke.knytning.sjekk.merknad.skilt-passer-ikke.bestill-skilt.lenke',
                                                    )}
                                                    text={t(
                                                        'personlig-kjennemerke.knytning.sjekk.merknad.skilt-passer-ikke.bestill-skilt.tekst',
                                                    )}
                                                />
                                            </li>
                                        );
                                    }
                                    return (
                                        <li key={feil}>
                                            {tilFeilmelding(feil)}
                                        </li>
                                    );
                                })}
                        </ul>
                    </SVVFormError>
                )}
            </div>
            <div aria-live="polite">
                {laster && (
                    <div className="responsive-spacing-top--xxsmall">
                        <SVVSpinner size="small" centered={false}>
                            {t('personlig-kjennemerke.delt.laster')}
                        </SVVSpinner>
                    </div>
                )}
            </div>
            <div className="responsive-spacing-top--xxsmall">
                <SVVTextLink
                    url="https://www.vegvesen.no/dinside/dinekjoretoy#/kjoretoy"
                    text={t('personlig-kjennemerke.knytt.dine-kjoretoy')}
                />
            </div>
        </div>
    );
};
